/**
 * Form values -> API payloads.
 *
 * Parity with the app matters more than elegance here: a divergence in how a
 * phone number is normalised produces two identities for one human.
 */

import {
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

import { validatePhoneNumber } from "./validationSchemas";
import { USAGE_TYPES } from "../profile/usageOptions";

export const DEFAULT_COUNTRY = "GH";

export const getDialCode = (iso2) => {
  try {
    return getCountryCallingCode(String(iso2 || "").toUpperCase());
  } catch {
    return null;
  }
};

/**
 * national digits + ISO2 -> E.164, matching the app's `toE164`: strip ALL
 * non-digits, then ALL leading zeros, then prefix the dial code.
 * `0240000167` in GH becomes `+233240000167`.
 *
 * The one addition over the app: a value the user already typed in
 * international form is parsed as-is. The app never sees one because its
 * field renders the `+CC` outside the input, but the web field is bare, and
 * prefixing the dial code a second time turned a pasted `+923081700808` into
 * `+233923081700808` — which then failed validation as an invalid number.
 * National input, the only shape the app produces, is unaffected.
 */
export const toE164 = (national, iso2) => {
  const raw = String(national || "").trim();

  if (raw.startsWith("+")) {
    const parsed = parsePhoneNumberFromString(raw);
    if (parsed) return parsed.number;

    const digits = raw.replace(/\D/g, "");
    return digits ? `+${digits}` : null;
  }

  const dial = getDialCode(iso2);
  if (!dial) return null;

  const digits = raw.replace(/\D/g, "").replace(/^0+/, "");

  return digits ? `+${dial}${digits}` : null;
};

/** E.164 -> country + national digits, for prefilling an edit form. */
export const parseE164 = (value) => {
  const parsed = parsePhoneNumberFromString(String(value || "").trim());

  if (!parsed) {
    return {
      countryCode: null,
      callingCode: null,
      nationalNumber: String(value || "").replace(/^\+/, ""),
    };
  }

  return {
    countryCode: parsed.country ?? null,
    callingCode: parsed.countryCallingCode ?? null,
    nationalNumber: parsed.nationalNumber ?? "",
  };
};

/**
 * Delegates to the schema validator so a number can never be accepted by the
 * form and then rejected here, or the reverse.
 */
export const isValidPhone = (national, iso2) =>
  validatePhoneNumber(national, iso2);

export const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export const isValidEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(value));

/**
 * A single token means firstName === lastName. That looks like a bug and is
 * not: the API rejects a blank lastName, and this is what the app sends, so
 * changing it here would create records the two clients disagree about.
 */
export const splitFullName = (fullName) => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: parts[0] };

  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
};

export const joinName = (firstName, lastName) =>
  [firstName, lastName].map((part) => String(part || "").trim()).filter(Boolean).join(" ");

/** Note the plural: OTHERS, not OTHER. */
export const toApiGender = (value) => {
  const gender = String(value || "").trim().toUpperCase();

  if (gender === "MALE") return "MALE";
  if (gender === "FEMALE") return "FEMALE";

  return "OTHERS";
};

export const toUsageType = (value) => {
  const usage = String(value || "").trim().toUpperCase();

  return USAGE_TYPES.includes(usage) ? usage : null;
};

/**
 * YYYY-MM-DD in LOCAL time. Never toISOString() — it shifts the day for
 * anyone east or west of UTC, which silently changes a date of birth.
 */
export const toIsoDate = (date) => {
  if (!date) return null;

  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) return null;

  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");

  return `${value.getFullYear()}-${month}-${day}`;
};

export const detectChannelType = (value) =>
  String(value || "").includes("@") ? "email" : "phone";

export const defaultOtpChannel = (channelType) =>
  channelType === "email" ? "email" : "sms";

/** The opposite channel. The second contact is never a choice. */
export const oppositeChannel = (channelType) =>
  channelType === "phone" ? "email" : "phone";

/**
 * Reactivation takes either verified contact, so the client decides which it
 * is rather than offering a toggle.
 */
export const buildIdentityKey = (input, countryCode) => {
  const trimmed = String(input || "").trim();
  if (!trimmed) return null;

  if (trimmed.includes("@")) return trimmed.toLowerCase();

  return toE164(trimmed, countryCode || DEFAULT_COUNTRY);
};

/**
 * `/addresses/from-location/` -> the address shape `/consumers/save/` accepts.
 *
 * The endpoint answers an ENVELOPE, not an address:
 *   { address: {...}, state: {...} | null, city: {...} | null }
 *
 * Forwarding that envelope as `address` is what made save answer 400
 * `invalid.request.format` / "Malformed JSON request" on web while mobile
 * worked: the server was handed an object where `city` and `stateCode` are
 * strings, and Jackson cannot bind it.
 *
 * Mirrors the app's `addressFromGeocode`, including reading city and state
 * from the sibling records when the address block itself omits them.
 *
 * @returns {Object|null} exactly the nine fields of AddressDto, minus `id`.
 */
export const addressFromGeocode = (geocoded, fallbackLabel) => {
  const address = geocoded?.address;
  if (!address) return null;

  return {
    address1: address.address1 || fallbackLabel || null,
    address2: address.address2 ?? null,
    postalCode: address.postalCode ?? null,
    city: address.city ?? geocoded?.city?.name ?? null,
    stateCode: address.stateCode ?? geocoded?.state?.stateCode ?? null,
    countryCode: address.countryCode ?? null,
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
    digitalCode: address.digitalCode ?? null,
  };
};

/**
 * The client-side address fallback, used when the geocoder is unavailable or
 * returns nothing usable.
 */
export const addressFromCoords = (label, coords, countryCode) => {
  if (!label && !coords) return null;

  return {
    address1: label ?? null,
    address2: null,
    postalCode: null,
    city: null,
    stateCode: null,
    countryCode: countryCode ?? null,
    latitude: coords?.latitude ?? null,
    longitude: coords?.longitude ?? null,
    digitalCode: null,
  };
};
